import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useUserRoles } from '../../../hooks/users';
import type { UserRole } from '../types/auth.type';
import { usePermissions } from '../contexts/permissions-context';

interface RefreshAuthTokenOptions {
  forceRefresh?: boolean;
}

/**
 * Hook for refreshing the Auth0 token.
 *
 * @returns An object containing the refresh function.
 */
export const useRefreshAuthToken = () => {
  const { getAccessTokenSilently } = useAuth0();

  const refresh = useCallback(
    async (options?: RefreshAuthTokenOptions) => {
      const { forceRefresh = false } = options ?? {};

      if (forceRefresh) {
        await getAccessTokenSilently({ cacheMode: 'off' });
      } else {
        await getAccessTokenSilently();
      }
    },
    [getAccessTokenSilently]
  );

  return { refresh };
};

/**
 * Hook for forcing a fresh token when you know permissions might have changed.
 * Use this after operations that could update user permissions.
 */
export const useForceTokenRefresh = () => {
  const { refresh } = useRefreshAuthToken();

  const forceRefresh = useCallback(async () => {
    await refresh({ forceRefresh: true });
  }, [refresh]);

  return { forceRefresh };
};

/**
 * Hook to check if the user has the required permissions.
 *
 * @param requiredPermissions - A string or an array of strings representing the required permissions.
 * @returns An object containing:
 * - hasPermission: A boolean indicating if the user has all the required permissions.
 * - loading: A boolean indicating if the permissions are still loading.
 */
export const useHasPermissions = (requiredPermissions: string | string[]) => {
  const { hasPermission, loading } = usePermissions();

  return {
    hasPermission: hasPermission(requiredPermissions),
    loading,
  };
};

/**
 * Hook to check if the user has the required roles.
 *
 * @param requiredRoles - A string or an array of strings representing the required roles.
 * @returns An object containing:
 * - hasRole: A boolean indicating if the user has all the required roles.
 * - loading: A boolean indicating if the roles are still loading.
 */
export const useHasRoles = (requiredRoles: UserRole | UserRole[]) => {
  const { roles, loading } = useUserRoles();

  const requiredRolesArray = Array.isArray(requiredRoles)
    ? requiredRoles
    : [requiredRoles];

  const hasRole = requiredRolesArray.every((role) => roles.includes(role));

  return { hasRole, loading };
};
